import { HttpClient, HttpContext } from '@angular/common/http'
import { Injectable } from '@angular/core'
import {
  HANDLE_ERROR_AND_THROW,
  HELIOS_COMMAND_HTTP_TOKEN,
} from '@helios/utils'
import { Observable, throwError } from 'rxjs'
import { catchError, shareReplay } from 'rxjs/operators'
import {
  CorporateActionsProcessingBatchRequest,
  CorporateActionsProcessingBatchResponse,
  RetainedProcessingGridState,
} from '../models/corporate-action-processing.model'

/**
 * Service for the Corporate Actions Processing tab (PVAL-25952).
 *
 * Deliberately a THIRD service file rather than new methods on
 * `corporate-actions.service.ts`: that file is the Clearwater feature's service
 * and is injected into the shared `CorporateActionsPanelComponent`, so touching
 * it widens the regression surface the reporter gated. The lib's CLAUDE.md
 * states the existing features "do not share ... a services layer".
 *
 * URLs are built off a local `CORPORATE_ACTIONS_HOST` constant exactly as
 * `ice-corporate-actions.service.ts` does for `ice/...`. The relative form
 * resolves through the existing nginx rule
 * (`/helios/helios-corporate-actions` -> `/api/corporate-actions/external`) to
 * `/api/corporate-actions/external/processing/*`, precisely the paths
 * PVAL-26137 adds — so no routing change belongs in this change set.
 *
 * EVERY request here sets `HANDLE_ERROR_AND_THROW`, and that is not optional
 * boilerplate — it is what makes this service's errors observable at all. The
 * app-wide `ErrorInterceptor` is registered as an `HTTP_INTERCEPTORS` multi
 * provider (`apps/helios/src/app/app.module.ts`) and, for any HTTP failure,
 * reports the error and then returns `EMPTY` — a COMPLETION, not an error —
 * unless the request opted out via `HANDLE_ERROR_AND_THROW` or a matching
 * `ERROR_INTERCEPTOR_BYPASS` status
 * (`apps/helios/src/app/interceptors/error.interceptor.ts`). Without the token:
 *
 * - `getAvailableCommands`' `catchError`-nulls-the-cache retry idiom never runs
 *   and the caller's error callback never fires, so the Processing tab sits on
 *   "Loading available commands…" forever with a dead toolbar — never
 *   `COMMANDS_LOAD_ERROR`, never `NO_COMMANDS_AVAILABLE`. Worse, this service is
 *   `providedIn: 'root'`, so the empty-completed `shareReplay(1)` would poison
 *   every panel instance for the whole page session even after PVAL-26137
 *   deploys;
 * - `submitBatch`'s failure classification (`isDefiniteServerRejection`,
 *   `SUBMIT_REJECTED_ERROR`, `SUBMIT_UNCONFIRMED_ERROR`) becomes unreachable:
 *   `toPromise()` resolves `undefined`, so `applyRowResults([], …)` runs and
 *   retires EVERY row UNCONFIRMED — including on the contract's whole-batch
 *   Jackson `400`, the one path that must stay editable and submittable because
 *   nothing was queued.
 *
 * This mirrors the repo convention and this ticket's named reference
 * implementation (`create-edit-transaction.service.ts` sets it on ~30 requests,
 * `command.service.ts` on 3, and
 * `libs/common-api/src/lib/services/http-commentary-counts.service.ts:18`
 * documents exactly this reason). NOTE the pre-existing sibling
 * `ice-corporate-actions.service.ts` has the same dead
 * `catchError`-nulls-the-cache idiom WITHOUT the token — that is where the
 * pattern was copied from, it needs its own ticket, and it must not be copied
 * further.
 */
const CORPORATE_ACTIONS_HOST = 'helios-corporate-actions'

export const AVAILABLE_COMMANDS_URL = `${CORPORATE_ACTIONS_HOST}/processing/availableCommands`
export const PROCESSING_COMMANDS_URL = `${CORPORATE_ACTIONS_HOST}/processing/commands`

@Injectable({
  providedIn: 'root',
})
export class CorporateActionsProcessingService {
  constructor(private readonly httpClient: HttpClient) {}

  private availableCommands$: Observable<string[]> | null = null

  /**
   * The Processing grid's hoisted state (PVAL-26303 #2). See
   * `RetainedProcessingGridState`.
   *
   * Here rather than in a new service because this one is already
   * `providedIn: 'root'`, so a single instance outlives every panel teardown in
   * the session. A plain field, not a `BehaviorSubject`: the one reader reads
   * once per component construction, and a stream would invite a second
   * subscriber writing back into the grid mid-session.
   */
  private retainedGridState: RetainedProcessingGridState | null = null

  /**
   * Store the grid's live state for the rest of the browser session.
   *
   * Replaces wholesale: the caller always passes the complete row set, and a
   * merge would have to guess at deletions. The hazard is a blank grid written
   * over a populated record, which is why the machine path never resets rows and
   * `resetRows` stays reachable from user gestures only.
   */
  retainGridState(state: RetainedProcessingGridState): void {
    this.retainedGridState = state
  }

  /**
   * The retained state for `commandType`, or `undefined` when there is nothing
   * to restore.
   *
   * No side effects: the state must still be there for the next teardown in the
   * session. A `commandType` mismatch answers `undefined` and the caller seeds a
   * fresh grid, because rows carry their own type's field set.
   */
  peekRetainedGridState(
    commandType: string,
  ): RetainedProcessingGridState | undefined {
    const retained = this.retainedGridState
    return retained?.commandType === commandType ? retained : undefined
  }

  /**
   * The command types the caller's CIAM policy allows, as a plain JSON array of
   * command-type enum names.
   *
   * Cached with the in-lib `shareReplay(1)` + null-the-cache-on-error idiom
   * (`IceCorporateActionsService.getConfig()`), so a failure is NOT cached: the
   * next subscriber re-fetches. That matters because the endpoint 404s until
   * PVAL-26137 deploys — a cached rejected Promise would never retry once the
   * wrapper lands.
   *
   * `HANDLE_ERROR_AND_THROW` is what makes that idiom (and the caller's error
   * callback) run at all — see the class docblock. Without it `ErrorInterceptor`
   * swallows the 404 into `EMPTY` and the cache holds a completed-empty stream
   * for the rest of the page session.
   */
  getAvailableCommands(): Observable<string[]> {
    if (!this.availableCommands$) {
      this.availableCommands$ = this.httpClient
        .get<string[]>(AVAILABLE_COMMANDS_URL, {
          context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true),
        })
        .pipe(
          catchError(err => {
            this.availableCommands$ = null
            return throwError(err)
          }),
          shareReplay(1),
        )
    }
    return this.availableCommands$
  }

  /**
   * Post the entire batch in ONE request.
   *
   * `HELIOS_COMMAND_HTTP_TOKEN` is what `commands-loading.interceptor.ts` reads
   * to wire the CommandsDrawer / loading state; without it the command never
   * appears in the drawer. The caller must additionally have awaited
   * `BlankSessionService.createBlankSessionIfNotInSession()` first, because the
   * endpoint takes `helios-session-id` as a required non-nullable header which
   * the session interceptor only supplies while a session is active.
   *
   * `HANDLE_ERROR_AND_THROW` is set alongside it so a failed POST actually
   * REJECTS. Without it `ErrorInterceptor` turns every failure into `EMPTY`,
   * `toPromise()` resolves `undefined`, and the whole failure-classification
   * path in `CorporateActionsProcessingComponent` is dead code — see the class
   * docblock. Note this also covers `CommandsLoadingInterceptor`'s synthetic
   * pre-flight 400 on a session with pending approvals, where literally nothing
   * left the browser and the rows must stay submittable.
   */
  submitBatch(
    request: CorporateActionsProcessingBatchRequest,
  ): Observable<CorporateActionsProcessingBatchResponse> {
    return this.httpClient.post<CorporateActionsProcessingBatchResponse>(
      PROCESSING_COMMANDS_URL,
      request,
      {
        context: new HttpContext()
          .set(HELIOS_COMMAND_HTTP_TOKEN, true)
          .set(HANDLE_ERROR_AND_THROW, true),
      },
    )
  }
}
